import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSearch } from "react-icons/fa";
import { AdminOrgDto, OrganizationRoleType, useListPersonsQuery } from "../../../autogen/bff-api";
import { setImpersonatedPersonId } from "../../../common/local-storage/impersonated-person";
import { clearOrganizationId } from "../../../common/local-storage/selected-organization";
import { Paging } from "../../../common/Paging";

interface Props {
  organization: AdminOrgDto;
  onClose: () => void;
}

export const AdminImpersonateUserModal = ({ organization, onClose }: Props) => {
  const { t } = useTranslation();
  const [skip, setSkip] = useState(0);
  const limit = 10;

  const [searchName, setSearchName] = useState("");
  const [debouncedName, setDebouncedName] = useState("");

  const [roles, setRoles] = useState<OrganizationRoleType[]>([]);

  const handleRolesChange = (roles: OrganizationRoleType[]) => {
    setRoles(roles);
  };

  const debouncedSetName = useMemo(
    () =>
      debounce((v: string) => {
        setDebouncedName(v.trim());
      }, 300),
    []
  );

  useEffect(() => {
    debouncedSetName(searchName);
    return () => {
      debouncedSetName.cancel();
    };
  }, [searchName, debouncedSetName]);

  const { data, isLoading, isFetching } = useListPersonsQuery({
    orgIds: [organization.id],
    roles: roles.length > 0 ? roles : undefined,
    name: debouncedName ? debouncedName : undefined,
    limit,
    skip,
  });

  const persons = useMemo(() => data?.persons ?? [], [data]);
  const count = data?.count;

  const handleImpersonateClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const personId = e.currentTarget.value;
    if (!personId || typeof personId !== "string") return;
    setImpersonatedPersonId(personId);
    clearOrganizationId();
    window.location.reload();
  };

  useEffect(() => {
    setSkip(0);
  }, [debouncedName, roles]);

  return (
    <Modal isOpen={true} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent maxH="80vh" overflow="hidden">
        <ModalHeader>{t("Impersonate user")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody display="flex" flexDirection="column" flex="1" minH="0">
          <Stack spacing={3} flex="1" minH="0">
            <InputGroup size="sm">
              <InputLeftElement>
                <Icon as={FaSearch} color="smMuted" />
              </InputLeftElement>
              <Input
                placeholder={String(t("Search by name"))}
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
              />
            </InputGroup>
            <Stack spacing={1}>
              <Text fontWeight="semibold">{t("Filter by roles")}</Text>
              <CheckboxGroup value={roles} onChange={handleRolesChange}>
                <HStack spacing={3} wrap="wrap">
                  {Object.values(OrganizationRoleType).map((r) => (
                    <Checkbox key={r} value={r} size="sm">
                      {t(r)}
                    </Checkbox>
                  ))}
                </HStack>
              </CheckboxGroup>
            </Stack>
            <Box flex="1" overflowY="auto" minH="0">
              <Stack spacing={2}>
                {persons.map((p) => (
                  <Flex
                    key={p.id}
                    align="center"
                    justify="space-between"
                    border="1px solid"
                    borderColor="smBorder"
                    p={2}
                    rounded="md"
                  >
                    <Stack spacing={0}>
                      <Text fontSize="sm" fontWeight="medium">
                        {p.email}
                      </Text>
                      <Text fontSize="xs" color="smMuted">
                        {p.firstName} {p.lastName}
                      </Text>
                    </Stack>
                    <Button
                      size="xs"
                      colorScheme="blue"
                      variant="outline"
                      value={p.id}
                      onClick={handleImpersonateClick}
                    >
                      {t("Impersonate")}
                    </Button>
                  </Flex>
                ))}
                {persons.length === 0 && !(isLoading || isFetching) ? (
                  <Text color="smMuted">{t("No users found")}</Text>
                ) : null}
              </Stack>
            </Box>
            <Paging
              limit={limit}
              skip={skip}
              setSkip={setSkip}
              isLoading={isLoading || isFetching}
              count={count || undefined}
            />
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
